import api from './api';

export interface NewsArticle {
  // WICHTIG: ALLES vom Backend muss nullable sein für maximale Robustheit!
  _id?: string;
  id?: string;
  title?: string;
  subtitle?: string;
  content?: string;
  contentHtml?: string;
  description?: string;
  image?: string;
  imageUrl?: string;
  thumbnail?: string;
  category?: string;
  source?: string;
  sourceUrl?: string;
  author?: string;
  // publishedAt kommt als ISO-String
  publishedAt?: string;
  // createdAt/updatedAt können Number (timestamp) ODER String sein
  createdAt?: string | number;
  updatedAt?: string | number;
  slug?: string;
  url?: string;
  views?: number;
  tags?: string[];
  contentCrawled?: boolean;
  active?: boolean;
  isCrawled?: boolean;
  aiGenerated?: boolean | null;
  /** Verknüpfter Live-Blog → dritter Reiter auf der Detailseite */
  liveBlogId?: string | null;
}

export interface NewsResponse {
  success?: boolean;
  data?: NewsArticle[];
  total?: number;
  page?: number;
  pages?: number;
}

class NewsService {
  // Neueste News-Artikel abrufen
  async getLatestNews(params?: {
    page?: number;
    limit?: number;
    offset?: number;
    source?: string;
  }): Promise<NewsResponse> {
    try {
      const response = await api.get('/api/news-articles', { 
        params: {
          limit: params?.limit || 20,
          offset: params?.offset || 0,
          source: params?.source
        }
      });
      
      // Response-Format anpassen
      if (response.data.articles) {
        return {
          success: true,
          data: response.data.articles,
          total: response.data.pagination?.total,
          page: params?.page || 1
        };
      }
      
      return response.data;
    } catch (error) {
      console.error('Fehler beim Abrufen der News:', error);
      throw error;
    }
  }

  // News-Artikel nach ID abrufen
  async getNewsById(id: string): Promise<{ success: boolean; data: NewsArticle | null }> {
    try {
      const response = await api.get(`/api/news-article/id/${id}`);
      return this.normalizeArticleResponse(response.data);
    } catch (error) {
      console.error('Fehler beim Abrufen des Artikels:', error);
      throw error;
    }
  }

  // News-Artikel nach Slug abrufen
  async getNewsBySlug(slug: string): Promise<{ success: boolean; data: NewsArticle | null }> {
    try {
      const response = await api.get(`/api/news-article/slug/${slug}`);
      return this.normalizeArticleResponse(response.data);
    } catch (error) {
      console.error('Fehler beim Abrufen des Artikels:', error);
      throw error;
    }
  }

  // Normalisiert die Response: Backend liefert entweder den Artikel direkt
  // oder als { success, data }-Wrapper
  private normalizeArticleResponse(
    raw: any
  ): { success: boolean; data: NewsArticle | null } {
    if (!raw || typeof raw !== 'object') {
      return { success: false, data: null };
    }
    // Wrapper-Format
    if ('data' in raw && raw.data && typeof raw.data === 'object') {
      return {
        success: raw.success !== false,
        data: raw.data as NewsArticle,
      };
    }
    // Direktes Artikel-Objekt (hat _id / id / slug / title)
    if (raw._id || raw.id || raw.slug || raw.title) {
      return { success: true, data: raw as NewsArticle };
    }
    return { success: false, data: null };
  }

  // News nach Quelle abrufen
  async getNewsBySource(source: string, params?: {
    page?: number;
    limit?: number;
  }): Promise<NewsResponse> {
    try {
      const response = await api.get(`/api/news-articles/source/${source}`, { params });
      return response.data;
    } catch (error) {
      console.error('Fehler beim Abrufen der News:', error);
      throw error;
    }
  }

  // Helper: Bild-URL generieren (100% robust)
  getImageUrl(article?: NewsArticle): string | null {
    if (!article) return null; // Signal für Fallback
    
    // Priorität: imageUrl > image > thumbnail
    if (article.imageUrl && typeof article.imageUrl === 'string' && article.imageUrl.trim()) {
      return article.imageUrl.trim();
    }
    
    if (article.image && typeof article.image === 'string' && article.image.trim()) {
      const imagePath = article.image.trim();
      // Falls es ein relativer Pfad ist, API-URL voranstellen
      if (imagePath.startsWith('http')) {
        return imagePath;
      }
      return `${api.defaults.baseURL}${imagePath.startsWith('/') ? '' : '/'}${imagePath}`;
    }
    
    if (article.thumbnail && typeof article.thumbnail === 'string' && article.thumbnail.trim()) {
      const thumbnailPath = article.thumbnail.trim();
      if (thumbnailPath.startsWith('http')) {
        return thumbnailPath;
      }
      return `${api.defaults.baseURL}${thumbnailPath.startsWith('/') ? '' : '/'}${thumbnailPath}`;
    }
    
    // Kein Bild vorhanden - return null für Fallback-Styling
    return null;
  }

  // Helper: Check if article has image (robust)
  hasImage(article?: NewsArticle): boolean {
    const imageUrl = this.getImageUrl(article);
    return imageUrl !== null;
  }

  /** Parst ein Datum (String oder Timestamp) zu einem Date-Objekt oder null. */
  parseDate(value?: string | number | null): Date | null {
    if (value === null || value === undefined || value === '') return null;
    try {
      if (typeof value === 'number') {
        const d = new Date(value);
        return isNaN(d.getTime()) ? null : d;
      }
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;
        // Rein numerischer String -> als Timestamp behandeln
        if (/^\d+$/.test(trimmed)) {
          const d = new Date(parseInt(trimmed, 10));
          return isNaN(d.getTime()) ? null : d;
        }
        const d = new Date(trimmed);
        return isNaN(d.getTime()) ? null : d;
      }
    } catch {
      return null;
    }
    return null;
  }

  /** Wählt das beste verfügbare Datum aus dem Artikel. */
  getArticleDate(article?: NewsArticle): Date | null {
    if (!article) return null;
    return (
      this.parseDate(article.publishedAt) ||
      this.parseDate(article.createdAt) ||
      this.parseDate(article.updatedAt)
    );
  }

  /** Formatiertes Datum wie Flutter ("7. Dez 2026"). */
  formatDate(article?: NewsArticle): string {
    const d = this.getArticleDate(article);
    if (!d) return '';
    const months = [
      'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
      'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez',
    ];
    return `${d.getDate()}. ${months[d.getMonth()]} ${d.getFullYear()}`;
  }

  // Helper: Zeit formatieren (robust) - akzeptiert String, Number oder Article
  formatTimeAgo(date?: string | number | null): string {
    const parsed = this.parseDate(date ?? null);
    if (!parsed) return '';

    try {
      const now = new Date();
      const diffMs = now.getTime() - parsed.getTime();

      if (diffMs < 0) return 'gerade eben';

      const diffMins = Math.floor(diffMs / 60000);
      if (diffMins < 1) return 'gerade eben';
      if (diffMins < 60) return `vor ${diffMins} Min.`;

      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `vor ${diffHours} Std.`;

      const diffDays = Math.floor(diffHours / 24);
      if (diffDays < 7) return diffDays === 1 ? 'vor 1 Tag' : `vor ${diffDays} Tagen`;

      // Ab einer Woche Datumsformat (wie Flutter relativeTime)
      const months = [
        'Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun',
        'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez',
      ];
      return `${parsed.getDate()}. ${months[parsed.getMonth()]} ${parsed.getFullYear()}`;
    } catch {
      return '';
    }
  }

  /** Convenience: formatTimeAgo für ein komplettes Artikel-Objekt. */
  formatArticleTime(article?: NewsArticle): string {
    const d = this.getArticleDate(article);
    if (!d) return '';
    return this.formatTimeAgo(d.getTime());
  }

  // Helper: Artikel-Titel formatieren (robust)
  formatTitle(article?: NewsArticle): string {
    if (!article) return 'Titel nicht verfügbar';
    if (article.title && typeof article.title === 'string' && article.title.trim()) {
      return article.title.trim();
    }
    return 'Kein Titel';
  }

  // Helper: Artikel-Beschreibung formatieren (robust)
  formatDescription(article?: NewsArticle): string {
    if (!article) return '';
    
    // Priorität: description > subtitle > content (gekürzt)
    if (article.description && typeof article.description === 'string' && article.description.trim()) {
      return article.description.trim();
    }
    
    if (article.subtitle && typeof article.subtitle === 'string' && article.subtitle.trim()) {
      return article.subtitle.trim();
    }
    
    if (article.content && typeof article.content === 'string' && article.content.trim()) {
      const content = article.content.trim();
      return content.length > 150 ? `${content.substring(0, 150)}...` : content;
    }
    
    return '';
  }

  // Helper: Views formatieren (robust)
  formatViews(views?: number): string {
    if (views === null || views === undefined || typeof views !== 'number') {
      return '0 Aufrufe';
    }
    
    if (isNaN(views) || views < 0) {
      return '0 Aufrufe';
    }
    
    try {
      if (views < 1000) return `${Math.floor(views)} Aufrufe`;
      if (views < 1000000) return `${(views / 1000).toFixed(1)}K Aufrufe`;
      return `${(views / 1000000).toFixed(1)}M Aufrufe`;
    } catch (error) {
      return '0 Aufrufe';
    }
  }

  // Helper: Kategorie-Farbe (robust)
  getCategoryColor(category?: string): string {
    if (!category || typeof category !== 'string') return 'text-gray-500';
    
    const colors: Record<string, string> = {
      'news': 'text-blue-500',
      'tournament': 'text-green-500',
      'interview': 'text-purple-500',
      'results': 'text-orange-500',
      'announcement': 'text-red-500'
    };
    
    return colors[category.toLowerCase().trim()] || 'text-gray-500';
  }

  /**
   * Helper: Versteckte Quellen (z. B. dartsnews) dürfen NIRGENDWO in der UI
   * erwähnt werden – weder als Badge/Label noch als Link.
   */
  isHiddenSource(article?: NewsArticle): boolean {
    if (!article) return false;
    const source = typeof article.source === 'string' ? article.source : '';
    const sourceUrl = typeof article.sourceUrl === 'string' ? article.sourceUrl : '';
    return (
      source.toLowerCase().includes('dartsnews') ||
      sourceUrl.toLowerCase().includes('dartsnews')
    );
  }

  /** Helper: Anzeigename der Quelle – null bei versteckter Quelle. */
  getDisplaySource(article?: NewsArticle): string | null {
    if (!article || this.isHiddenSource(article)) return null;
    return article.source ?? null;
  }

  // Helper: Safe Array-Zugriff für News-Listen
  getSafeNewsArray(data?: NewsArticle[]): NewsArticle[] {
    if (!data || !Array.isArray(data)) return [];
    return data.filter(article => article && typeof article === 'object');
  }
}

export default new NewsService();